import React from 'react';

import { table } from '@gitbook/doc-core';
import type { TableSelection } from '@gitbook/doc-core/src/table/selection';
import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import type { Change } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { shortcuts } from '../../../../../../Keyboard';
import { createColumnDefsPalette } from '../../../ColumnDefinitions';
import type { TableRecordKV, TableState } from '../../../types';
import { relativeSelectionMove } from '../../utils';
import { BulkInsertRowsDialog } from '../BulkInsertRowsDialog';

export interface UseTableRowOptionsParams {
    record: TableRecordKV;
    beforeOrderIndex?: string;
    afterOrderIndex?: string;
    isLastRow?: boolean;
    editor: EditorContainer;
    tableKey: string;
    openRecord: (recordId: string) => void;
    selection: TableSelection;
    tableState?: TableState;
}

export interface UseTableRowOptionsReturn {
    paletteHandle: ReturnType<typeof usePaletteHandle>;
    paletteButtonRef: React.RefObject<HTMLButtonElement>;
    onPressOptions: () => void;
    BulkInsertRowsDialogElement: () => React.ReactElement;
}

export function useTableRowOptions(params: UseTableRowOptionsParams): UseTableRowOptionsReturn {
    const {
        record,
        beforeOrderIndex,
        afterOrderIndex,
        isLastRow,
        editor,
        tableKey,
        openRecord,
        selection,
        tableState,
    } = params;

    // Dialog trigger state for bulk insert rows dialog
    const dialogTrigger = useDialogTriggerState();
    const [insertDirection, setInsertDirection] = React.useState<'above' | 'below'>('above');

    // Centralized palette handle for row options
    const paletteButtonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(paletteButtonRef, {
        onClose: () => {
            // Restore focus to the table cell when palette closes
            if (selection.recordId && selection.definitionId) {
                editor.change((change) => {
                    table.moveTableSelection(change, tableKey, {
                        recordId: selection.recordId!,
                        definitionId: selection.definitionId!,
                    });
                    change.focus();
                });
            }
        },
    });

    // Create the onPressOptions function to be shared by RowSettings and RecordValue
    const onPressOptions = React.useCallback(() => {
        paletteHandle.open(
            createPalette({
                search: { placeholder: 'Select action…' },
                groups: [
                    {
                        key: 'table.row',
                        commands: [
                            {
                                key: 'table.row.open',
                                title: `Edit row fields`,
                                icon: Icon.Maximize,
                                trigger: () => {
                                    openRecord(record.key);
                                },
                            },
                            {
                                key: 'table.row.insertAbove',
                                title: 'Insert above',
                                icon: Icon.TableInsertRowAbove,
                                trigger: () => {
                                    editor.change((change) => {
                                        table.insertEmptyRecord(change, tableKey, {
                                            after: record.value.orderIndex,
                                            before: beforeOrderIndex,
                                        });
                                    });
                                },
                                descendant: () => {
                                    return createPalette({
                                        groups: [
                                            {
                                                key: 'insert.above',
                                                commands: [
                                                    {
                                                        key: 'insert.above.1',
                                                        title: 'Insert 1 row',
                                                        icon: Icon.TableInsertRowAbove1,
                                                        trigger: () => {
                                                            editor.change((change) => {
                                                                table.insertEmptyRecord(
                                                                    change,
                                                                    tableKey,
                                                                    {
                                                                        after: record.value
                                                                            .orderIndex,
                                                                        before: beforeOrderIndex,
                                                                    }
                                                                );
                                                            });
                                                        },
                                                    },
                                                    {
                                                        key: 'insert.above.multiple',
                                                        title: 'Insert multiple',
                                                        icon: Icon.TableInsertRowAboveX,
                                                        trigger: () => {
                                                            setInsertDirection('above');
                                                            dialogTrigger.open();
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                    });
                                },
                            },
                            {
                                key: 'table.row.insertBelow',
                                title: 'Insert below',
                                icon: Icon.TableInsertRow,
                                trigger: () => {
                                    editor.change((change) => {
                                        table.insertEmptyRecord(
                                            change,
                                            tableKey,
                                            !isLastRow
                                                ? {
                                                      after: record.value.orderIndex,
                                                      before: afterOrderIndex,
                                                  }
                                                : { before: record.value.orderIndex }
                                        );
                                    });
                                },
                                descendant: () => {
                                    return createPalette({
                                        groups: [
                                            {
                                                key: 'insert.below',
                                                commands: [
                                                    {
                                                        key: 'insert.below.1',
                                                        title: 'Insert 1 row',
                                                        icon: Icon.TableInsertRowBelow1,
                                                        trigger: () => {
                                                            editor.change((change) => {
                                                                table.insertEmptyRecord(
                                                                    change,
                                                                    tableKey,
                                                                    !isLastRow
                                                                        ? {
                                                                              after: record.value
                                                                                  .orderIndex,
                                                                              before: afterOrderIndex,
                                                                          }
                                                                        : {
                                                                              before: record.value
                                                                                  .orderIndex,
                                                                          }
                                                                );
                                                            });
                                                        },
                                                    },
                                                    {
                                                        key: 'insert.below.multiple',
                                                        title: 'Insert multiple',
                                                        icon: Icon.TableInsertRowBelowX,
                                                        trigger: () => {
                                                            setInsertDirection('below');
                                                            dialogTrigger.open();
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                    });
                                },
                            },
                            {
                                key: 'table.row.addColumn',
                                icon: Icon.AddColumnRight,
                                title: 'Add column',
                                trigger: () => {
                                    editor.change((change) => {
                                        table.insertColumn(
                                            change,
                                            tableKey,
                                            table.createColumnFromType('text')
                                        );
                                    });
                                },
                                descendant: () => {
                                    return createColumnDefsPalette((type) => {
                                        editor.change((change) => {
                                            table.insertColumn(
                                                change,
                                                tableKey,
                                                table.createColumnFromType(type)
                                            );
                                        });
                                    });
                                },
                            },
                            tableState
                                ? {
                                      key: 'table.row.remove',
                                      title: 'Delete row',
                                      icon: Icon.Trashcan,
                                      trigger: () => {
                                          editor.change((change) => {
                                              deleteRowAndMoveSelection(
                                                  change,
                                                  tableKey,
                                                  tableState,
                                                  record.key
                                              );
                                          });
                                      },
                                      keyboardShortcut: shortcuts.TABLE_ROW_DELETE,
                                  }
                                : null,
                        ].filter(filterOutNullable),
                    },
                ],
            })
        );
    }, [
        record,
        beforeOrderIndex,
        afterOrderIndex,
        isLastRow,
        editor,
        tableKey,
        openRecord,
        paletteHandle,
        tableState,
        dialogTrigger,
    ]);

    const BulkInsertRowsDialogElement = () => {
        return (
            <DialogRoot {...dialogTrigger.rootProps}>
                <BulkInsertRowsDialog
                    editor={editor}
                    tableKey={tableKey}
                    record={record}
                    direction={insertDirection}
                    beforeOrderIndex={beforeOrderIndex}
                    afterOrderIndex={afterOrderIndex}
                    isLastRow={isLastRow}
                    onClose={() => dialogTrigger.close()}
                />
            </DialogRoot>
        );
    };

    return {
        paletteHandle,
        paletteButtonRef,
        onPressOptions,
        BulkInsertRowsDialogElement,
    };
}

// Delete the current row and move selection to the next row (or prior if it was the last) within the same column
export const deleteRowAndMoveSelection = (
    change: Change,
    tableKey: string,
    tableState: TableState,
    recordKey: string
) => {
    // Try to move to the next row first, keeping the same column
    if (
        !relativeSelectionMove(change, tableKey, tableState, {
            definition: { relative: 0 }, // Keep same column
            record: { relative: 1 }, // Move to next row
        })
    ) {
        // If no next row, try moving to previous row
        relativeSelectionMove(change, tableKey, tableState, {
            definition: { relative: 0 }, // Keep same column
            record: { relative: -1 }, // Move to previous row
        });
    }
    table.removeRecord(change, tableKey, recordKey);
};
